import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Table,
  Form,
  Row,
  Col,
  Card,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTimes,
  faTrash,
  faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import IconAlert from '../../../../../common/IconAlert';
import api from '@services/api';
import { toast } from 'react-toastify';

const ManageEntityModal = ({
  show,
  onHide,
  salonId,
  entities,
  allEntities,
  onEntitiesUpdate,
  onOpenParticipants,
  entityType
}) => {
  // Déterminer si nous sommes en train de gérer des partenaires ou des sociétés
  const isPartner = entityType === 'partner';

  // Textes adaptés au type d'entité
  const entityText = {
    title: isPartner
      ? 'Gérer les partenaires du salon'
      : 'Gérer les sociétés du salon',
    currentList: isPartner
      ? 'Partenaires actuels du salon'
      : 'Sociétés actuelles du salon',
    noEntity: isPartner
      ? 'Aucun partenaire associé à ce salon'
      : 'Aucune société associée à ce salon',
    add: isPartner ? 'Ajouter un partenaire' : 'Ajouter une société',
    select: isPartner
      ? 'Sélectionner un partenaire'
      : 'Sélectionner une société',
    entityName: isPartner ? 'Nom du partenaire' : 'Nom de la société',
    infoText: isPartner
      ? "Ajoutez les partenaires qui seront présents à ce salon et désignez un responsable pour chacun d'eux. Le responsable pourra saisir les informations relatives aux voeux de rendez-vous."
      : "Ajoutez les sociétés qui seront présentes à ce salon et désignez un responsable pour chacune d'elles. Le responsable pourra saisir les informations relatives aux voeux de rendez-vous."
  };

  // API endpoint et clés adaptés au type d'entité
  const apiConfig = {
    endpoint: `/admin/partner-salons/${salonId}/entities/${
      isPartner ? 'partners' : 'companies'
    }`,
    entityName: isPartner ? 'partners' : 'companies', // Ceci est utilisé pour la clé dans le payload, c'est correct
    participantName: isPartner ? 'partnerParticipants' : 'companyParticipants',
    updateSuccess: isPartner
      ? 'Les partenaires et participants ont été mis à jour'
      : 'Les sociétés et participants ont été mis à jour',
    updateError: isPartner
      ? 'Erreur lors de la mise à jour des partenaires'
      : 'Erreur lors de la mise à jour des sociétés'
  };

  const [localEntities, setLocalEntities] = useState([]);

  useEffect(() => {
    if (show) {
      setLocalEntities(
        entities ? entities.map(e => ({ ...e, pivot: { ...e.pivot } })) : []
      );
    }
  }, [entities, show]); // participants retiré des dépendances

  // Gérer la sélection d'une entité à ajouter
  const handleEntityChange = selectedOption => {
    if (!selectedOption) return;

    const entity = allEntities.find(e => e.id === selectedOption.value);

    if (entity) {
      const newLocalEntities = [...localEntities];
      if (!newLocalEntities.some(e => e.id === entity.id)) {
        newLocalEntities.push(entity);
        setLocalEntities(newLocalEntities);
      }
    }
  };

  const handleResponsibleChange = (entityId, selectedOption) => {
    const newLocalEntities = localEntities.map(entity => {
      if (entity.id === entityId) {
        return {
          ...entity,
          pivot: {
            ...entity.pivot,
            responsible_id: selectedOption ? selectedOption.value : null
          }
        };
      }
      return entity;
    });

    setLocalEntities(newLocalEntities);
  };

  const removeEntity = entityId => {
    const newLocalEntities = localEntities.filter(
      entity => entity.id !== entityId
    );
    setLocalEntities(newLocalEntities);
  };

  const handleSaveEntities = async () => {
    try {
      const entitiesDataToSave = localEntities.map(entity => ({
        id: entity.id,
        responsible_id: entity.pivot?.responsible_id || null
      }));

      const payload = {
        entities: entitiesDataToSave
      };

      const response = await api.put(apiConfig.endpoint, payload);

      if (response.data.success) {
        toast(apiConfig.updateSuccess);
        onEntitiesUpdate(response.data.data);
        onHide();
      } else {
        toast(response.data.message || apiConfig.updateError);
      }
    } catch (error) {
      console.error(
        `Erreur lors de la mise à jour des entités (${entityType}):`,
        error
      );
      toast(
        error.response?.data?.message || error.message || apiConfig.updateError
      );
    }
  };

  const handleCancel = () => {
    setLocalEntities(
      entities ? entities.map(e => ({ ...e, pivot: { ...e.pivot } })) : []
    );
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="xl" backdrop="static">
      <Modal.Header className="border-bottom">
        <Modal.Title>{entityText.title}</Modal.Title>
        <div className="d-flex">
          <Button
            variant="primary"
            className="admin-primary me-2"
            onClick={handleSaveEntities}
          >
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Enregistrer
          </Button>
          <Button variant="light" className="p-1" onClick={handleCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <IconAlert variant="info" className="mb-3">
            <p className="mb-0">{entityText.infoText}</p>
          </IconAlert>
        </div>

        {/* Liste des entités actuelles du salon */}
        <h5 className="mb-3">{entityText.currentList}</h5>
        <Table striped bordered hover responsive className="mb-8">
          <thead className="text-center">
            <tr>
              <th style={{ width: '20%', padding: '12px' }}>
                {entityText.entityName}
              </th>
              <th style={{ width: '30%' }}>Responsable</th>
              <th style={{ width: '15%' }}>Status</th>
              <th style={{ width: '20%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {localEntities && localEntities.length > 0 ? (
              localEntities.map(entity => (
                <tr key={entity.id}>
                  <td>
                    <span style={{ marginLeft: '12px', display: 'block' }}>
                      {entity.name}
                    </span>
                  </td>
                  <td>
                    <Select
                      options={(
                        allEntities.find(e => e.id === entity.id)?.users || []
                      ).map(u => ({
                        value: u.id,
                        label: `${u.firstname} ${u.lastname}`
                      }))}
                      onChange={selectedOption =>
                        handleResponsibleChange(entity.id, selectedOption)
                      }
                      placeholder="Sélectionner un responsable..."
                      value={
                        entity.pivot?.responsible_id
                          ? {
                              value: entity.pivot.responsible_id,
                              label: (() => {
                                const entityData = allEntities.find(
                                  e => e.id === entity.id
                                );
                                const user = entityData?.users?.find(
                                  u => u.id === entity.pivot.responsible_id
                                );
                                return user
                                  ? `${user.firstname} ${user.lastname}`
                                  : 'Responsable assigné';
                              })()
                            }
                          : null
                      }
                    />
                  </td>
                  <td className="text-center">
                    {entity.wishes_count > 0 ? (
                      <Badge bg="success">
                        {entity.wishes_count} voeux envoyés
                      </Badge>
                    ) : (
                      <Badge bg="warning">Aucun vœu envoyé</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2 justify-content-center">
                      <Button
                        variant="falcon-info"
                        size="sm"
                        onClick={() => onOpenParticipants(entity)}
                      >
                        <FontAwesomeIcon
                          icon={faUserFriends}
                          className="me-1"
                        />
                        {entity.participants_count} Participant
                        {entity.participants_count > 1 ? 's' : ''}
                      </Button>
                      <Button
                        variant="falcon-danger"
                        size="sm"
                        onClick={() => removeEntity(entity.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">
                  {entityText.noEntity}
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Ajouter une entité */}
        <Card className="mb-4 mt-4">
          <Card.Header className="bg-light">
            <h5 className="mb-0">{entityText.add}</h5>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-end">
              <Col md={8}>
                <Form.Group controlId="entity_select">
                  <Form.Label>{entityText.select}</Form.Label>
                  <Select
                    options={allEntities
                      .filter(e => !localEntities.some(le => le.id === e.id))
                      .map(e => ({ value: e.id, label: e.name }))}
                    onChange={handleEntityChange}
                    placeholder={entityText.select}
                    isClearable
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleCancel}>
          Annuler
        </Button>
        <Button
          variant="primary"
          className="admin-primary"
          onClick={handleSaveEntities}
        >
          <FontAwesomeIcon icon={faEdit} className="me-2" />
          Enregistrer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ManageEntityModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  salonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, // salonId peut être un nombre
  entities: PropTypes.array,
  allEntities: PropTypes.array,
  // participants: PropTypes.array, // Prop participants n'est plus utilisée pour la sauvegarde ici
  onEntitiesUpdate: PropTypes.func.isRequired,
  onOpenParticipants: PropTypes.func,
  entityType: PropTypes.oneOf(['partner', 'company']).isRequired
};

ManageEntityModal.defaultProps = {
  entities: [],
  allEntities: []
  // participants: [] // Prop participants n'est plus utilisée pour la sauvegarde ici
};

export default ManageEntityModal;
